import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiArrowLeft,
  FiPaperclip,
  FiSend,
  FiUser,
  FiUserCheck,
  FiChevronDown,
  FiLock,
  FiX,
  FiExternalLink,
  FiAlignLeft,
  FiCalendar
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { ticketsAPI, notificationsAPI } from '../../services/api';
import { connectSocket } from '../../services/socket';
import { useAuth } from '../../context/AuthContext';
import './TicketDetail.css';

const STATUS_OPTIONS = ['OPEN', 'IN_PROGRESS', 'ON_HOLD', 'RESOLVED', 'CLOSED'];

function getInitials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function formatTime(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
}

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function statusClass(s) {
  switch (String(s || '').toUpperCase()) {
    case 'OPEN':
      return 'badge-new';
    case 'IN_PROGRESS':
      return 'badge-meeting';
    case 'ON_HOLD':
      return 'badge-deferred';
    case 'RESOLVED':
      return 'badge-qualified';
    case 'CLOSED':
      return 'badge-closed';
    default:
      return 'badge-new';
  }
}

function priorityClass(p) {
  switch (String(p || '').toUpperCase()) {
    case 'HIGH':
      return 'badge-not-interested';
    case 'MEDIUM':
      return 'badge-followup';
    case 'LOW':
      return 'badge-b2c';
    default:
      return 'badge-followup';
  }
}

const PAGE_SIZE = 30;

const TicketDetail = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { user } = useAuth();
  const uid = user?.user_id;
  const role = String(user?.role || '').toLowerCase();
  const canInternalNote = ['super_admin', 'admin', 'manager'].includes(role);

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [msgPage, setMsgPage] = useState(1);
  const [msgTotalPages, setMsgTotalPages] = useState(1);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyFiles, setReplyFiles] = useState([]);
  const [isInternal, setIsInternal] = useState(false);
  const [sending, setSending] = useState(false);
  const [statusEditing, setStatusEditing] = useState('');
  const scrollRef = useRef(null);
  const atBottomRef = useRef(true);
  const initialScrollDone = useRef(false);
  const fileInputRef = useRef(null);

  const statusLabel = (code) => {
    const c = String(code || '').toUpperCase();
    const map = {
      OPEN: t('tickets.statusOpen'),
      IN_PROGRESS: t('tickets.statusInProgress'),
      ON_HOLD: t('tickets.statusOnHold'),
      RESOLVED: t('tickets.statusResolved'),
      CLOSED: t('tickets.statusClosed')
    };
    return map[c] || code;
  };

  const loadTicket = useCallback(async () => {
    const res = await ticketsAPI.get(id);
    setTicket(res.data);
    setStatusEditing(res.data.status || 'OPEN');
  }, [id]);

  const loadMessagesPage = useCallback(
    async (page, prepend) => {
      const res = await ticketsAPI.getMessages(id, { page, limit: PAGE_SIZE });
      const data = res.data?.data || [];
      const totalPages = res.data?.totalPages || 1;
      setMsgTotalPages(totalPages);
      if (prepend) {
        setMessages((prev) => {
          const seen = new Set(prev.map((m) => m.message_id));
          const merged = data.filter((m) => !seen.has(m.message_id));
          return [...merged, ...prev];
        });
      } else {
        setMessages(data);
      }
      return { totalPages };
    },
    [id]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        await loadTicket();
        if (!cancelled) {
          await loadMessagesPage(1, false);
        }
      } catch (err) {
        if (!cancelled) toast.error(t('tickets.toasts.loadFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
      notificationsAPI.markTicketRead()
        .then(() => window.dispatchEvent(new Event('ticket-mark-read')))
        .catch(() => {});
    })();
    return () => {
      cancelled = true;
    };
  }, [loadTicket, loadMessagesPage, t]);

  useEffect(() => {
    if (!messages.length || initialScrollDone.current) return;
    const el = scrollRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
      initialScrollDone.current = true;
    }
  }, [messages]);

  const joinId = id != null ? Number(id) : null;

  useEffect(() => {
    if (joinId == null || Number.isNaN(joinId)) return;
    const s = connectSocket();
    s.emit('ticket:join', joinId);

    const onNew = (payload) => {
      if (!payload || Number(payload.ticket_id) !== joinId) return;
      setMessages((prev) => {
        if (prev.some((m) => m.message_id === payload.message_id)) return prev;
        return [...prev, payload];
      });
      if (atBottomRef.current && scrollRef.current) {
        requestAnimationFrame(() => {
          const el = scrollRef.current;
          if (el) el.scrollTop = el.scrollHeight;
        });
      }
    };

    const onUpdated = (payload) => {
      if (!payload || Number(payload.ticket_id) !== joinId) return;
      setTicket((prev) => (prev ? { ...prev, ...payload } : prev));
      setStatusEditing(payload.status || '');
    };

    s.on('ticket:new_message', onNew);
    s.on('ticket:updated', onUpdated);

    return () => {
      s.emit('ticket:leave', joinId);
      s.off('ticket:new_message', onNew);
      s.off('ticket:updated', onUpdated);
    };
  }, [joinId]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    atBottomRef.current = nearBottom;

    if (el.scrollTop < 60 && msgPage < msgTotalPages && !loadingOlder) {
      const prevHeight = el.scrollHeight;
      setLoadingOlder(true);
      const nextPage = msgPage + 1;
      loadMessagesPage(nextPage, true)
        .then(() => {
          setMsgPage(nextPage);
          requestAnimationFrame(() => {
            if (scrollRef.current) {
              scrollRef.current.scrollTop = scrollRef.current.scrollHeight - prevHeight;
            }
          });
        })
        .catch(() => {})
        .finally(() => setLoadingOlder(false));
    }
  };

  const removeReplyFile = (index) => {
    setReplyFiles((prev) => {
      const next = prev.filter((_, i) => i !== index);
      if (next.length === 0 && fileInputRef.current) fileInputRef.current.value = '';
      return next;
    });
  };

  const sendReply = async (e) => {
    e.preventDefault();
    const text = replyText.trim();
    if (!text && !replyFiles.length) {
      toast.error(t('tickets.toasts.emptyMessage'));
      return;
    }
    setSending(true);
    try {
      const fd = new FormData();
      if (text) fd.append('body', text);
      if (isInternal && canInternalNote) fd.append('is_internal', '1');
      replyFiles.forEach((f) => fd.append('files', f));
      await ticketsAPI.postMessage(id, fd);
      setReplyText('');
      setReplyFiles([]);
      setIsInternal(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      const res = await ticketsAPI.getMessages(id, { page: 1, limit: PAGE_SIZE });
      setMessages(res.data?.data || []);
      setMsgPage(1);
      setMsgTotalPages(res.data?.totalPages || 1);
      requestAnimationFrame(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      });
    } catch (err) {
      toast.error(err.response?.data?.message || t('tickets.toasts.sendFailed'));
    } finally {
      setSending(false);
    }
  };

  const saveStatus = async () => {
    if (!ticket || statusEditing === ticket.status) return;
    try {
      await ticketsAPI.patch(id, { status: statusEditing });
      toast.success(t('tickets.toasts.statusUpdated'));
      await loadTicket();
    } catch (err) {
      toast.error(err.response?.data?.message || t('tickets.toasts.updateFailed'));
    }
  };

  if (loading) {
    return (
      <div className="page-container ticket-detail-page ticket-detail-page--loading">
        <div className="loading-spinner" style={{ padding: '3rem' }}>
          <div className="spinner" />
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="page-container ticket-detail-page ticket-detail-page--fill">
        <div className="card ticket-detail-not-found">
          <p>{t('tickets.notFound')}</p>
          <Link to="/tickets" className="btn btn-primary">
            {t('tickets.backToList')}
          </Link>
        </div>
      </div>
    );
  }

  const studentName = [ticket.student_first_name, ticket.student_last_name].filter(Boolean).join(' ');
  const studentLabel = ticket.student_id
    ? `${studentName || ticket.student_ref} (${ticket.student_ref})`
    : null;
  const agencyLabel = ticket.agency_id
    ? `${ticket.agency_display_name || ticket.agency_ref} (${ticket.agency_ref})`
    : null;

  return (
    <div className="page-container ticket-detail-page ticket-detail-page--fill">
      <Link to="/tickets" className="ticket-back-link">
        <FiArrowLeft size={18} />
        {t('tickets.backToList')}
      </Link>

      <div className="card ticket-detail-summary">
        <div className="ticket-detail-summary__head">
          <div className="ticket-detail-title-block">
            <span className="ticket-ref-pill">{ticket.ticket_ref || `#${ticket.ticket_id}`}</span>
            <h1 className="ticket-detail-title">{ticket.title}</h1>
          </div>
          <div className="ticket-detail-badges">
            <span className={`badge ${statusClass(ticket.status)}`}>{statusLabel(ticket.status)}</span>
            <span className={`badge ${priorityClass(ticket.priority)}`}>{ticket.priority}</span>
          </div>
        </div>

        <div className="ticket-detail-meta-grid">
          <div className="ticket-meta-tile">
            <span className="ticket-meta-tile__icon" aria-hidden>
              <FiUser size={16} />
            </span>
            <div className="ticket-meta-tile__body">
              <span className="ticket-meta-tile__label">{t('tickets.createdBy')}</span>
              <span className="ticket-meta-tile__value">{ticket.created_by_name || '—'}</span>
            </div>
          </div>
          <div className="ticket-meta-tile">
            <span className="ticket-meta-tile__icon" aria-hidden>
              <FiUserCheck size={16} />
            </span>
            <div className="ticket-meta-tile__body">
              <span className="ticket-meta-tile__label">{t('tickets.colAssignee')}</span>
              <span className="ticket-meta-tile__value">{ticket.assigned_to_name || t('tickets.unassigned')}</span>
            </div>
          </div>
          <div className="ticket-meta-tile">
            <span className="ticket-meta-tile__icon" aria-hidden>
              <FiCalendar size={16} />
            </span>
            <div className="ticket-meta-tile__body">
              <span className="ticket-meta-tile__label">{t('tickets.colCreatedAt')}</span>
              <span className="ticket-meta-tile__value">{formatTime(ticket.created_at) || '—'}</span>
            </div>
          </div>
        </div>

        <div className="ticket-detail-entities">
          {ticket.student_id && (
            <Link to={`/students/${ticket.student_id}`} className="ticket-entity-pill">
              <FiExternalLink size={14} />
              {studentLabel}
            </Link>
          )}
          {ticket.agency_id && (
            <Link to={`/b2b-agencies/${ticket.agency_id}`} className="ticket-entity-pill">
              <FiExternalLink size={14} />
              {agencyLabel}
            </Link>
          )}
          {!ticket.student_id && !ticket.agency_id && (
            <span className="ticket-detail-no-tag">{t('tickets.noTag')}</span>
          )}
        </div>

        {ticket.remarks != null && String(ticket.remarks).trim() !== '' && (
          <div className="ticket-detail-remarks">
            <div className="ticket-detail-remarks__head">
              <FiAlignLeft size={14} aria-hidden />
              <span>{t('tickets.fields.remarks')}</span>
            </div>
            <div className="ticket-detail-remarks__text">{String(ticket.remarks).trim()}</div>
          </div>
        )}

        <div className="ticket-detail-status-bar">
          <span className="ticket-detail-status-bar__label">{t('tickets.changeStatus')}</span>
          <div className="ticket-status-select-wrap">
            <select
              className="ticket-status-select-input"
              value={statusEditing}
              onChange={(e) => setStatusEditing(e.target.value)}
              aria-label={t('tickets.changeStatus')}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {statusLabel(s)}
                </option>
              ))}
            </select>
            <FiChevronDown className="ticket-status-select-chevron" aria-hidden />
          </div>
          <button
            type="button"
            className="btn btn-sm btn-primary ticket-status-apply"
            onClick={saveStatus}
            disabled={statusEditing === ticket.status}
          >
            {t('tickets.applyStatus')}
          </button>
        </div>
      </div>

      <div className="ticket-detail-thread">
        <div className="ticket-thread-header">
          <span className="ticket-thread-header__title">{t('tickets.conversationTitle')}</span>
        </div>
        <div className="ticket-messages-scroll" ref={scrollRef} onScroll={handleScroll}>
          {msgPage < msgTotalPages && (
            <div className="ticket-load-older">
              <span className="ticket-load-older__text">
                {loadingOlder ? t('common.loading') : t('tickets.scrollForOlder')}
              </span>
            </div>
          )}
          {messages.map((m) => {
            const own = uid && Number(m.sender_id) === Number(uid);
            const internal = Number(m.is_internal) === 1;
            return (
              <div key={m.message_id} className={`ticket-msg ${own ? 'ticket-msg--own' : ''}`}>
                <div className="ticket-msg-avatar">{getInitials(m.sender_name)}</div>
                <div className={`ticket-msg-bubble ${internal ? 'ticket-msg-bubble--internal' : ''}`}>
                  <div className="ticket-msg-meta">
                    <strong>{m.sender_name || '?'}</strong>
                    <span className="ticket-msg-time">{formatTime(m.created_at)}</span>
                    {internal && (
                      <span className="ticket-internal-badge">{t('tickets.internalNote')}</span>
                    )}
                  </div>
                  {m.body && <div className="ticket-msg-body">{m.body}</div>}
                  {m.attachments?.length > 0 && (
                    <div className="ticket-attachments">
                      {m.attachments.map((a, i) => {
                        const mime = String(a.mime_type || '');
                        const isImg = mime.startsWith('image/');
                        const url = a.download_url;
                        return (
                          <div key={a.attachment_id || i}>
                            {isImg && url ? (
                              <a href={url} target="_blank" rel="noopener noreferrer">
                                <img src={url} alt="" className="ticket-attach-preview" />
                              </a>
                            ) : (
                              <a href={url || '#'} target="_blank" rel="noopener noreferrer" className="ticket-attach-file-link">
                                {t('common.download')}: {a.file_name} ({formatFileSize(a.size_bytes)})
                              </a>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="ticket-reply-area">
          <form className="ticket-reply-form" onSubmit={sendReply}>
            {canInternalNote && (
              <label className={`ticket-internal-toggle ${isInternal ? 'ticket-internal-toggle--on' : ''}`}>
                <input
                  type="checkbox"
                  checked={isInternal}
                  onChange={(e) => setIsInternal(e.target.checked)}
                />
                <FiLock size={14} aria-hidden />
                <span>{t('tickets.internalNote')}</span>
              </label>
            )}

            {replyFiles.length > 0 && (
              <div className="ticket-reply-file-list">
                {replyFiles.map((f, i) => (
                  <span key={`${f.name}-${i}`} className="ticket-reply-file-chip">
                    <FiPaperclip size={12} aria-hidden />
                    <span className="ticket-reply-file-name">{f.name}</span>
                    <button
                      type="button"
                      className="ticket-reply-file-remove"
                      onClick={() => removeReplyFile(i)}
                      aria-label={t('common.remove')}
                    >
                      <FiX size={14} />
                    </button>
                  </span>
                ))}
              </div>
            )}

            <div className="ticket-reply-editor">
              <textarea
                className="ticket-reply-textarea"
                rows={2}
                placeholder={t('tickets.replyPlaceholder')}
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
              />
              <div className="ticket-reply-actions">
                <label className="ticket-reply-attach-btn" title={t('tickets.attachFiles')}>
                  <FiPaperclip size={16} aria-hidden />
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    aria-label={t('tickets.attachFiles')}
                    onChange={(e) => setReplyFiles(Array.from(e.target.files || []))}
                  />
                </label>
                <button type="submit" className="ticket-reply-send-btn" disabled={sending}>
                  <FiSend size={16} />
                  <span>{sending ? t('common.loading') : t('tickets.sendReply')}</span>
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TicketDetail;
